import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { fetchJson } from "../../api/rest";
import type {
	FieldDescriptor,
	FilterClause,
	SegmentSummary,
} from "../../types/planner";
import { CategorySelectModal } from "../categories/CategorySelectModal";
import {
	OrderedPicker,
	type PickerItem,
} from "../recommendations/OrderedPicker";

const OP_LABELS: Record<string, string> = {
	eq: "is",
	ne: "is not",
	lt: "<",
	lte: "≤",
	gt: ">",
	gte: "≥",
	between: "between",
	contains: "contains",
	starts_with: "starts with",
	in: "is one of",
	not_in: "is not one of",
	is: "is",
	before: "before",
	after: "after",
	within_days: "within the last N days",
	not_in_segment: "not already in",
};

// No batch-resolve endpoint exists for merchant/category names (unlike
// searchProducts/searchCategories in tracking/recommendations.ts), so a value
// added by id before a name was ever seen falls back to "#id" rather than a
// second round trip per id.
const hashString = (s: string): number => {
	let h = 0;
	for (let i = 0; i < s.length; i++)
		h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
	return Math.abs(h) || 1;
};

const inputClass =
	"rounded-lg border border-slate-200 px-2.5 py-1.5 text-[13px] outline-none focus:border-primary";

// ── Value editors, one per (type, op) shape ──────────────────

const NumberInput = ({
	value,
	onChange,
}: {
	value: unknown;
	onChange: (v: number) => void;
}) => (
	<input
		type="number"
		className={`${inputClass} w-28`}
		value={typeof value === "number" ? value : ""}
		onChange={(e) => onChange(e.target.valueAsNumber)}
	/>
);

const BetweenInput = ({
	value,
	onChange,
}: {
	value: unknown;
	onChange: (v: [number, number]) => void;
}) => {
	const pair = Array.isArray(value) ? value : [undefined, undefined];
	return (
		<div className="flex items-center gap-1.5">
			<input
				type="number"
				className={`${inputClass} w-20`}
				value={typeof pair[0] === "number" ? pair[0] : ""}
				onChange={(e) => onChange([e.target.valueAsNumber, pair[1] as number])}
			/>
			<span className="text-xs text-slate-400">and</span>
			<input
				type="number"
				className={`${inputClass} w-20`}
				value={typeof pair[1] === "number" ? pair[1] : ""}
				onChange={(e) => onChange([pair[0] as number, e.target.valueAsNumber])}
			/>
		</div>
	);
};

const TextInput = ({
	value,
	onChange,
}: {
	value: unknown;
	onChange: (v: string) => void;
}) => (
	<input
		type="text"
		className={`${inputClass} w-44`}
		value={typeof value === "string" ? value : ""}
		onChange={(e) => onChange(e.target.value)}
	/>
);

const BoolSelect = ({
	value,
	onChange,
}: {
	value: unknown;
	onChange: (v: boolean) => void;
}) => (
	<select
		className={inputClass}
		value={value ? "true" : "false"}
		onChange={(e) => onChange(e.target.value === "true")}
	>
		<option value="true">Yes</option>
		<option value="false">No</option>
	</select>
);

const DateInput = ({
	value,
	onChange,
}: {
	value: unknown;
	onChange: (v: string) => void;
}) => (
	<input
		type="date"
		className={inputClass}
		value={typeof value === "string" ? value : ""}
		onChange={(e) => onChange(e.target.value)}
	/>
);

const EnumCheckboxes = ({
	options,
	value,
	onChange,
}: {
	options: string[];
	value: unknown;
	onChange: (v: string[]) => void;
}) => {
	const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
	const toggle = (opt: string) => {
		const next = new Set(selected);
		if (next.has(opt)) next.delete(opt);
		else next.add(opt);
		onChange([...next]);
	};
	return (
		<div className="flex flex-wrap gap-2">
			{options.map((opt) => (
				<label
					key={opt}
					className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[13px] text-slate-600"
				>
					<input
						type="checkbox"
						checked={selected.has(opt)}
						onChange={() => toggle(opt)}
					/>
					{opt}
				</label>
			))}
		</div>
	);
};

// Free-text tag entry via OrderedPicker's search adapter — there is no
// distinct-value endpoint for color/size/gender (only /products/brands
// exists), so typing a value and picking the single "Add …" suggestion is
// the whole interaction; it never calls the server.
const manualEntrySearch = async (q: string): Promise<PickerItem[]> => {
	const term = q.trim();
	return term ? [{ id: hashString(term), label: term }] : [];
};

const brandSearch = async (q: string): Promise<PickerItem[]> => {
	const res = await fetchJson<{
		items: { brand: string; productCount: number }[];
	}>(`/products/brands?q=${encodeURIComponent(q)}&limit=20`);
	return res.items.map((r) => ({
		id: hashString(r.brand),
		label: r.brand,
		sub: String(r.productCount),
	}));
};

const merchantSearch = async (q: string): Promise<PickerItem[]> => {
	const res = await fetchJson<{ items: { id: number; name: string }[] }>(
		`/merchants?q=${encodeURIComponent(q)}&limit=20`,
	);
	return res.items.map((r) => ({ id: r.id, label: r.name }));
};

const StringListPicker = ({
	field,
	value,
	onChange,
}: {
	field: FieldDescriptor;
	value: unknown;
	onChange: (v: string[]) => void;
}) => {
	const items: PickerItem[] = (
		Array.isArray(value) ? (value as string[]) : []
	).map((v) => ({ id: hashString(v), label: v }));
	return (
		<OrderedPicker
			title={field.label}
			hint={
				field.key === "brand"
					? "Search brands…"
					: `Type a ${field.label.toLowerCase()}, press enter to add`
			}
			items={items}
			ordered={false}
			search={field.key === "brand" ? brandSearch : manualEntrySearch}
			onChange={(next) => onChange(next.map((i) => i.label))}
		/>
	);
};

const MerchantPicker = ({
	value,
	onChange,
}: {
	value: unknown;
	onChange: (v: number[]) => void;
}) => {
	const ids = Array.isArray(value) ? (value as number[]) : [];
	const [labels, setLabels] = useState<Record<number, string>>({});
	const items: PickerItem[] = ids.map((id) => ({
		id,
		label: labels[id] ?? `#${id}`,
	}));
	return (
		<OrderedPicker
			title="Merchants"
			hint="Search merchants…"
			items={items}
			ordered={false}
			search={async (q) => {
				const found = await merchantSearch(q);
				setLabels((prev) => {
					const next = { ...prev };
					for (const f of found) next[f.id] = f.label;
					return next;
				});
				return found;
			}}
			onChange={(next) => onChange(next.map((i) => i.id))}
		/>
	);
};

const CategoryPicker = ({
	value,
	includeDescendants,
	onChange,
	onIncludeDescendantsChange,
}: {
	value: unknown;
	includeDescendants: boolean | undefined;
	onChange: (v: number[]) => void;
	onIncludeDescendantsChange: (v: boolean) => void;
}) => {
	const ids = Array.isArray(value) ? (value as number[]) : [];
	const [open, setOpen] = useState(false);
	const [labels, setLabels] = useState<Record<number, string>>({});
	// Tracks which ids a fetch has already been kicked off for, so the effect
	// can key on the stable "ids.join(",")" string without also depending on
	// `labels` (which it writes to — a self-referential dependency).
	const requested = useRef<Set<number>>(new Set());

	useEffect(() => {
		const missing = ids.filter((id) => !requested.current.has(id));
		if (missing.length === 0) return;
		for (const id of missing) requested.current.add(id);
		let cancelled = false;
		Promise.all(
			missing.map((id) =>
				fetchJson<{ name: string } | null>(`/categories/${id}/detail`).catch(
					() => null,
				),
			),
		).then((results) => {
			if (cancelled) return;
			setLabels((prev) => {
				const next = { ...prev };
				missing.forEach((id, i) => {
					next[id] = results[i]?.name ?? `#${id}`;
				});
				return next;
			});
		});
		return () => {
			cancelled = true;
		};
	}, [ids]);

	return (
		<div className="flex flex-wrap items-center gap-2">
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="rounded-lg border border-dashed border-slate-300 px-2.5 py-1.5 text-[13px] text-slate-500 hover:border-primary hover:text-primary"
			>
				+ Add category
			</button>
			{ids.map((id) => (
				<span
					key={id}
					className="flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs text-slate-600"
				>
					{labels[id] ?? `#${id}`}
					<button
						type="button"
						onClick={() => onChange(ids.filter((x) => x !== id))}
						className="text-slate-400 hover:text-danger"
					>
						×
					</button>
				</span>
			))}
			<label className="flex items-center gap-1 text-xs text-slate-500">
				<input
					type="checkbox"
					checked={Boolean(includeDescendants)}
					onChange={(e) => onIncludeDescendantsChange(e.target.checked)}
				/>
				include subcategories
			</label>
			<CategorySelectModal
				open={open}
				onClose={() => setOpen(false)}
				excludeIds={ids}
				title="Add category to filter"
				onSelect={(categoryId) => {
					if (!ids.includes(categoryId)) onChange([...ids, categoryId]);
				}}
			/>
		</div>
	);
};

const SegmentSelect = ({
	segments,
	value,
	onChange,
}: {
	segments: SegmentSummary[];
	value: unknown;
	onChange: (v: number) => void;
}) => (
	<select
		className={inputClass}
		value={typeof value === "number" ? value : ""}
		onChange={(e) => onChange(Number(e.target.value))}
	>
		<option value="" disabled>
			Choose a segment…
		</option>
		{segments.map((s) => (
			<option key={s.id} value={s.id}>
				{s.name}
			</option>
		))}
	</select>
);

// ── The row itself ───────────────────────────────────────────

interface FilterRowProps {
	filter: FilterClause;
	fields: FieldDescriptor[];
	segments: SegmentSummary[];
	onChange: (next: FilterClause) => void;
	onRemove: () => void;
}

export const FilterRow = ({
	filter,
	fields,
	segments,
	onChange,
	onRemove,
}: FilterRowProps) => {
	const field = fields.find((f) => f.key === filter.field) ?? fields[0];
	if (!field) return null;

	const groups = [...new Set(fields.map((f) => f.group))];

	const setField = (key: string) => {
		const next = fields.find((f) => f.key === key);
		if (!next) return;
		onChange({ field: key, op: next.ops[0], value: undefined });
	};

	const setOp = (op: string) => onChange({ ...filter, op, value: undefined });
	const setValue = (value: unknown) => onChange({ ...filter, value });

	const renderValue = () => {
		if (field.type === "segment") {
			return (
				<SegmentSelect
					segments={segments}
					value={filter.value}
					onChange={setValue}
				/>
			);
		}
		if (field.type === "category") {
			return (
				<CategoryPicker
					value={filter.value}
					includeDescendants={filter.includeDescendants}
					onChange={setValue}
					onIncludeDescendantsChange={(v) =>
						onChange({ ...filter, includeDescendants: v })
					}
				/>
			);
		}
		if (field.type === "merchant") {
			return <MerchantPicker value={filter.value} onChange={setValue} />;
		}
		if (field.type === "enum") {
			return (
				<EnumCheckboxes
					options={field.enumValues ?? []}
					value={filter.value}
					onChange={setValue}
				/>
			);
		}
		if (field.type === "bool") {
			return <BoolSelect value={filter.value} onChange={setValue} />;
		}
		if (field.type === "date") {
			if (filter.op === "within_days") {
				return <NumberInput value={filter.value} onChange={setValue} />;
			}
			return <DateInput value={filter.value} onChange={setValue} />;
		}
		if (field.type === "string") {
			if (filter.op === "in" || filter.op === "not_in") {
				return (
					<StringListPicker
						field={field}
						value={filter.value}
						onChange={setValue}
					/>
				);
			}
			return <TextInput value={filter.value} onChange={setValue} />;
		}
		// number
		if (filter.op === "between") {
			return <BetweenInput value={filter.value} onChange={setValue} />;
		}
		return <NumberInput value={filter.value} onChange={setValue} />;
	};

	return (
		<div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2">
			<select
				className={inputClass}
				value={field.key}
				onChange={(e) => setField(e.target.value)}
			>
				{groups.map((g) => (
					<optgroup key={g} label={g}>
						{fields
							.filter((f) => f.group === g)
							.map((f) => (
								<option key={f.key} value={f.key}>
									{f.label}
								</option>
							))}
					</optgroup>
				))}
			</select>
			<select
				className={inputClass}
				value={filter.op}
				onChange={(e) => setOp(e.target.value)}
			>
				{field.ops.map((op) => (
					<option key={op} value={op}>
						{OP_LABELS[op] ?? op}
					</option>
				))}
			</select>
			{renderValue()}
			<button
				type="button"
				onClick={onRemove}
				className="ml-auto rounded-lg p-1.5 text-slate-400 hover:bg-danger/10 hover:text-danger"
				aria-label="Remove filter"
			>
				<Trash2 className="h-4 w-4" />
			</button>
		</div>
	);
};
